import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import {
  createStripeCheckoutSession,
  verifyCheckoutSession,
  createStripePortalSession,
  createStripeSubscription,
} from '../../util/api';

// ================ Async thunks ================ //

export const initiateCheckoutThunk = createAsyncThunk(
  'SubscriptionPlanPage/initiateCheckout',
  (_, { rejectWithValue }) => {
    return createStripeCheckoutSession()
      .then(response => {
        return response;
      })
      .catch(e => {
        const error = storableError(e);
        log.error(error, 'create-stripe-checkout-session-failed');
        return rejectWithValue(error);
      });
  }
);

export const createSubscriptionThunk = createAsyncThunk(
  'SubscriptionPlanPage/createSubscription',
  (listingId, { rejectWithValue }) => {
    if (!listingId) {
      return rejectWithValue({ message: 'listingId is required to create a per-listing subscription.' });
    }
    return createStripeSubscription({ listingId })
      .then(response => response)
      .catch(e => {
        const error = storableError(e);
        log.error(error, 'create-stripe-subscription-failed');
        return rejectWithValue(error);
      });
  }
);

export const verifyCheckoutThunk = createAsyncThunk(
  'SubscriptionPlanPage/verifyCheckout',
  (payload, { rejectWithValue }) => {
    const { sessionId, subscriptionId, listingId } = payload;
    return verifyCheckoutSession({ sessionId, subscriptionId, listingId })
      .then(response => response)
      .catch(e => {
        const error = storableError(e);
        log.error(error, 'verify-stripe-checkout-session-failed');
        return rejectWithValue(error);
      });
  }
);

export const openPortalThunk = createAsyncThunk(
  'SubscriptionPlanPage/openPortal',
  (_, { rejectWithValue }) => {
    return createStripePortalSession()
      .then(response => {
        return response;
      })
      .catch(e => {
        const error = storableError(e);
        log.error(error, 'create-stripe-portal-session-failed');
        return rejectWithValue(error);
      });
  }
);

export const publishListingThunk = createAsyncThunk(
  'SubscriptionPlanPage/publishListing',
  (listingId, { rejectWithValue, extra: sdk }) => {
    return sdk.ownListings
      .publishDraft({ id: listingId }, { expand: true })
      .then(response => response)
      .catch(e => {
        const error = storableError(e);
        log.error(error, 'publish-listing-after-subscription-failed');
        return rejectWithValue(error);
      });
  }
);

// Backward compatible wrappers
export const initiateCheckout = () => dispatch => {
  return dispatch(initiateCheckoutThunk()).unwrap();
};

export const createSubscription = listingId => dispatch => {
  return dispatch(createSubscriptionThunk(listingId)).unwrap();
};

export const verifyCheckout = payload => dispatch => {
  return dispatch(verifyCheckoutThunk(payload)).unwrap();
};

export const openPortal = () => dispatch => {
  return dispatch(openPortalThunk()).unwrap();
};

export const publishListing = listingId => dispatch => {
  return dispatch(publishListingThunk(listingId)).unwrap();
};

// ================ Slice ================ //

const subscriptionPlanPageSlice = createSlice({
  name: 'SubscriptionPlanPage',
  initialState: {
    checkoutInProgress: false,
    checkoutError: null,
    subscribeInProgress: false,
    subscribeError: null,
    verifyInProgress: false,
    verifyError: null,
    verifySuccess: false,
    portalInProgress: false,
    portalError: null,
    publishInProgress: false,
    publishError: null,
    publishedListingData: null,
  },
  reducers: {
    clearErrors: state => {
      state.checkoutError = null;
      state.subscribeError = null;
      state.verifyError = null;
      state.portalError = null;
      state.publishError = null;
    }
  },
  extraReducers: builder => {
    builder
      // Checkout
      .addCase(initiateCheckoutThunk.pending, state => {
        state.checkoutInProgress = true;
        state.checkoutError = null;
      })
      .addCase(initiateCheckoutThunk.fulfilled, state => {
        state.checkoutInProgress = false;
      })
      .addCase(initiateCheckoutThunk.rejected, (state, action) => {
        state.checkoutInProgress = false;
        state.checkoutError = action.payload;
      })
      // Custom Subscription
      .addCase(createSubscriptionThunk.pending, state => {
        state.subscribeInProgress = true;
        state.subscribeError = null;
      })
      .addCase(createSubscriptionThunk.fulfilled, state => {
        state.subscribeInProgress = false;
      })
      .addCase(createSubscriptionThunk.rejected, (state, action) => {
        state.subscribeInProgress = false;
        state.subscribeError = action.payload;
      })
      // Verify
      .addCase(verifyCheckoutThunk.pending, state => {
        state.verifyInProgress = true;
        state.verifyError = null;
        state.verifySuccess = false;
      })
      .addCase(verifyCheckoutThunk.fulfilled, state => {
        state.verifyInProgress = false;
        state.verifySuccess = true;
      })
      .addCase(verifyCheckoutThunk.rejected, (state, action) => {
        state.verifyInProgress = false;
        state.verifyError = action.payload;
      })
      // Publish listing after subscription
      .addCase(publishListingThunk.pending, state => {
        state.publishInProgress = true;
        state.publishError = null;
        state.publishedListingData = null;
      })
      .addCase(publishListingThunk.fulfilled, (state, action) => {
        state.publishInProgress = false;
        const listing = action.payload?.data?.data;
        if (listing) {
          state.publishedListingData = {
            id: listing.id?.uuid,
            title: listing.attributes?.title,
          };
        }
      })
      .addCase(publishListingThunk.rejected, (state, action) => {
        state.publishInProgress = false;
        state.publishError = action.payload;
      })
      // Portal
      .addCase(openPortalThunk.pending, state => {
        state.portalInProgress = true;
        state.portalError = null;
      })
      .addCase(openPortalThunk.fulfilled, state => {
        state.portalInProgress = false;
      })
      .addCase(openPortalThunk.rejected, (state, action) => {
        state.portalInProgress = false;
        state.portalError = action.payload;
      });
  },
});

export const { clearErrors } = subscriptionPlanPageSlice.actions;

export default subscriptionPlanPageSlice.reducer;
